/**
 * 加盟店側-端末管理 検索フォーム
 * メモ：端末管理の検索フォームを表示する
 */
import React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import ja from "date-fns/locale/ja";
import moment from "moment";
import clsx from "clsx";
import {
  FormControl,
  FormLabel,
  FormControlLabel,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@material-ui/core";

import {
  formStyles,
  checkboxStyles,
} from "../../../../common/components/styles";

import { TerminalSearchCondition } from "../../types";

import { GUEST_COLOR, FONT } from "../../../../../constants/common";
import { TERMINAL_TYPE_LIST } from "../../../../../constants/terminal";

import CustomTextField from "../../../../common/components/atoms/CustomTextField";
import CustomSelect from "../../../../common/components/molecules/CustomSelect";
import VariableCustomCheckbox from "../../../../common/components/atoms/VariableCustomCheckbox";
import PatternMatchChip from "../../../../common/components/atoms/PatternMatchChip";

registerLocale("ja", ja);

const useStyles = makeStyles(() => ({
  datepicker: {
    padding: "12px",
    width: "100%",
    backgroundColor: "#f2f7fb",
    fontSize: "1rem",
    borderWidth: 0,
    outlineWidth: 1,
  },
  label: {
    width: 800,
    textAlign: "left",
    padding: "0!important",
    height: 36,
  },
  table: {
    width: 800,
    minWidth: 800,
  },
  td: {
    padding: 1,
    backgroundColor: "#fff",
    color: "#000!important",
    borderTop: "1px solid #d2d5d8",
    borderBottom: "1px solid #d2d5d8",
    borderRight: "1px solid #d2d5d8",
    width: 400,
  },
  th: {
    padding: 0,
    fontFamily: FONT,
    backgroundColor: "#fff",
    color: "#000!important",
    width: 200,
    border: "1px solid #d2d5d8",
    position: "relative",
    paddingLeft: 20,
  },
  checkbox: {
    margin: "10px 20px",
  },
}));

// 検索条件フォーム設定
const terminalSearchFormData = [
  {
    name: "加盟店名",
    formData: "customerName",
    matchType: "部分一致",
  },
  {
    name: "加盟店コード",
    formData: "customerCode",
    matchType: "前方一致",
  },
  {
    name: "端末識別番号",
    formData: "termiIdentNum",
    matchType: "完全一致",
  },
  {
    name: "端末種別",
    formData: "terminalType",
    matchType: "完全一致",
  },
  {
    name: "初回DLL日",
    formData: "firstDLLDate",
    matchType: "前方一致",
  },
  {
    name: "解約済みの端末",
    formData: "terminalTerminationFlag",
    matchType: "",
  },
];

// 端末検索入力フォームのインターフェース
interface Props {
  searchCondition: TerminalSearchCondition;
  setSearchCondition: (arg: TerminalSearchCondition) => void;
}

const TerminalSearchConditionForm: React.FC<Props> = (props: Props) => {
  const classes = useStyles();
  const formClasses = formStyles({
    color: GUEST_COLOR.base,
    backgroundColor: GUEST_COLOR.primary,
  });
  const checkboxClasses = checkboxStyles({
    backgroundColor: GUEST_COLOR.primary,
  });

  const { searchCondition, setSearchCondition } = props;

  // 端末分類のプルダウン内容
  const terminalTypeArray = Array.from(TERMINAL_TYPE_LIST.keys());
  terminalTypeArray.unshift("指定なし");

  return (
    <FormControl className={formClasses.form}>
      <FormLabel className={clsx(formClasses.formLabel, classes.label)}>
        端末検索
      </FormLabel>
      <TableContainer>
        <Table
          className={clsx(formClasses.table, classes.table)}
          aria-label="simple table"
        >
          <TableBody>
            {terminalSearchFormData.map((formItem) => (
              <TableRow key={formItem.name}>
                <TableCell
                  align="left"
                  className={clsx(formClasses.th, classes.th)}
                >
                  {formItem.name}
                  {formItem.matchType && (
                    <PatternMatchChip
                      left={153}
                      top="24%"
                      text={formItem.matchType}
                    />
                  )}
                </TableCell>
                <TableCell align="left" className={classes.td}>
                  {/* 端末種別 */}
                  {formItem.formData === "terminalType" && (
                    <CustomSelect
                      value={searchCondition[formItem.formData] || "指定なし"}
                      pulldownArray={terminalTypeArray}
                      width="100%"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchCondition({
                          ...searchCondition,
                          [formItem.formData]: e.target.value,
                        })
                      }
                    />
                  )}
                  {/* 解約済み含むチェックボックス  */}
                  {formItem.formData === "terminalTerminationFlag" && (
                    <FormControlLabel
                      className={clsx(
                        checkboxClasses.checkbox,
                        classes.checkbox
                      )}
                      control={
                        <VariableCustomCheckbox
                          name={formItem.name}
                          value={formItem.formData}
                          checked={searchCondition.terminalTerminationFlag}
                          onChange={() =>
                            setSearchCondition({
                              ...searchCondition,
                              terminalTerminationFlag:
                                !searchCondition.terminalTerminationFlag,
                            })
                          }
                        />
                      }
                      label="含む"
                    />
                  )}
                  {/* 初回DLL日  */}
                  {formItem.formData === "firstDLLDate" && (
                    <DatePicker
                      selected={
                        searchCondition.firstDLLDate
                          ? new Date(searchCondition.firstDLLDate)
                          : null
                      }
                      onChange={(date: Date | null) =>
                        setSearchCondition({
                          ...searchCondition,
                          [formItem.formData]: date
                            ? moment(date).format("YYYY/MM/DD")
                            : "",
                        })
                      }
                      dateFormat="yyyy/MM/dd"
                      locale="ja"
                      className={classes.datepicker}
                      placeholderText="YYYY/MM/DD"
                    />
                  )}
                  {/* その他の項目 */}
                  {formItem.formData !== "terminalType" &&
                    formItem.formData !== "terminalTerminationFlag" &&
                    formItem.formData !== "firstDLLDate" && (
                      <CustomTextField
                        value={String(
                          searchCondition[
                            formItem.formData as keyof TerminalSearchCondition
                          ]
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setSearchCondition({
                            ...searchCondition,
                            [formItem.formData]: e.target.value,
                          })
                        }
                      />
                    )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </FormControl>
  );
};

export default TerminalSearchConditionForm;
